/**
 * localStorage service - Wrapper for browser localStorage with JSON support and error handling
 *
 * Provides a consistent API for storing and retrieving values from localStorage
 * with automatic JSON serialization/deserialization and graceful error handling.
 */

/**
 * Get a value from localStorage
 * @param {string} key - The storage key
 * @param {any} defaultValue - Default value if key doesn't exist or parsing fails
 * @returns {any} The stored value or defaultValue
 */
function getItem(key, defaultValue = null) {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;

    // Try to parse as JSON, fall back to raw string
    try {
      return JSON.parse(item);
    } catch {
      return item;
    }
  } catch (error) {
    // localStorage might be unavailable (private browsing, etc.)
    return defaultValue;
  }
}

/**
 * Set a value in localStorage
 * @param {string} key - The storage key
 * @param {any} value - The value to store (will be JSON stringified if not a string)
 * @returns {boolean} Whether the operation succeeded
 */
function setItem(key, value) {
  try {
    const serialized = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    // localStorage might be full or unavailable
    return false;
  }
}

/**
 * Remove a value from localStorage
 * @param {string} key - The storage key
 * @returns {boolean} Whether the operation succeeded
 */
function removeItem(key) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Create a namespaced storage helper for a specific feature
 * @param {string} namespace - The namespace prefix (e.g., "inbox-mailbox")
 * @returns {object} Object with get, set, remove methods scoped to namespace
 */
function createNamespacedStorage(namespace) {
  const buildKey = (key) => `${namespace}-${key}`;

  return {
    get: (key, defaultValue = null) => getItem(buildKey(key), defaultValue),
    set: (key, value) => setItem(buildKey(key), value),
    remove: (key) => removeItem(buildKey(key)),
  };
}

// -----------------------------------------------------------------------------
// Public API - Pre-configured namespaced storages
// Add new exports here as needed. Keep internal functions private until required.
// -----------------------------------------------------------------------------

export default {
  inboxMailbox: createNamespacedStorage('inbox-mailbox'),
  inboxSidebarExpanded: createNamespacedStorage('inbox-sidebar-expanded'),
  devTools: createNamespacedStorage('dev-tools'),
  // Add more namespaced storages here as needed:
  // userPreferences: createNamespacedStorage("user-preferences"),
};
